/**
 * theme — the `HouseAdapter` for the wall's UI layer, kept separate from `@core/wall/house.ts`
 * (the server-side render adapter plan c owns) per plan d § Files: "`HouseTheme` type;
 * `products/toile/wall/theme.ts` with the `--tl-*` values." A UI theme is presentation only — verb
 * wording, doors, whether a pencil exists, the image-URL builders — never a render contract, so it
 * lives in `@core/wall/ui` (imported by `Wall.tsx` and friends) rather than beside the server
 * adapter it has no dependency on.
 *
 * Toile and Ovid are both wired (`products/toile/wall/theme.ts`, `products/ovid/wall/theme.tsx`);
 * Narcis is not yet. The type is shaped around the axes the boards showed diverging: the grid vs.
 * justified split (BRIEF §2), the pencil door (§5), and the ratio picker (§ Ratio, Toile-only).
 */
import type {ReactNode} from 'react'
import type {AllowanceState, Identity, RatioKey} from '@core/wall/contract'
import type {HouseFacets} from '@core/wall/facets'
import type {ProductId} from '@core/products/registry'
import type {VerbState} from './chains'

/** Derived from `ProductId` (`@core/products/registry`), not a hand-written union — see
 *  `HouseAdapter.product` (`@core/wall/house.ts`) for the same alias on the server side. */
export type HouseId = Exclude<ProductId, 'quies'>

/** What a door shows: a small line above, and the word on the pill under it. The board
 *  (`thinking/design/toile_wall/OvidVeilStates.dc.html`, state "2 · hover") draws both on a veiled
 *  cell; `line: null` is the operator's 2026-09-17 ruling for an open one — the pill alone, since
 *  the shade by itself read as unfinished and the estate has no words a visitor may see. */
export type HouseDoor = {line: string | null; cta: string}

/**
 * The ghost's words at rest (board `FirstGlance`, validated 2026-09-18): two sentences in two
 * tones. `rule` — "Pick any picture and change it." — is the wall's one stated rule, the primary
 * text colour; `own` — "Or start from nothing." — is this cell's own act, in tungsten. `rule: null`
 * is a house with no rule to state: the ghost is then the single-sentence cell, pen and `own`
 * centred.
 */
export type GhostAtRest = {rule: string | null; own: string}

/**
 * The ghost's words once armed, before anything is typed — the dashed frame of the from-scratch
 * column. The same two tones as at rest, in the order of the moment: `own` — this cell's act, in
 * the accent — then `rule`, the wall's rule returning as a promise about the picture to come, in
 * the primary text colour. The field below already asks the question (`placeholder.onBlank`); the
 * frame says what happens next, so the two never repeat each other. `rule: null` leaves `own` alone.
 */
export type GhostArmed = {own: string; rule: string | null}

/**
 * The ghost's words at every house. A house states its own only to differ (`verbs.ghostAtRest` /
 * `verbs.ghostArmed`); `ghostWords` is the one read.
 */
export const DEFAULT_GHOST_AT_REST: GhostAtRest = {
    rule: 'Pick any picture and change it.',
    own: 'Or start from nothing.',
}
export const DEFAULT_GHOST_ARMED: GhostArmed = {
    own: 'Start with a few words.',
    rule: 'Then change it, like any picture here.',
}

export function ghostWords(theme: HouseTheme): {atRest: GhostAtRest; armed: GhostArmed} {
    return {
        atRest: theme.verbs.ghostAtRest ?? DEFAULT_GHOST_AT_REST,
        armed: theme.verbs.ghostArmed ?? DEFAULT_GHOST_ARMED,
    }
}

/**
 * The wall's small print around the column and sheet: the way back (`back.lead`, then the
 * desktop or phone gesture), the header search's accessible name, the pencil's accessible name,
 * and the bookmark door's three titles (`pin.off` offers it, `pin.on` removes it, `pin.idle` names
 * the inert one). A house states its own only to differ (`HouseTheme.chrome`).
 */
export type WallChrome = {
    back: {lead: string; desktop: string; phone: string}
    search: string
    pencil: string
    pin: {off: string; on: string; idle: string}
    /** The accessible name of the intake overlay (`Wall`'s `renderIdentityIntake`). */
    intake: string
}

export const DEFAULT_CHROME: WallChrome = {
    back: {lead: '↓ Back', desktop: 'scroll · or click outside', phone: '· swipe down'},
    search: 'Search',
    pencil: 'Describe a change',
    pin: {off: 'Bookmark this version', on: 'Remove bookmark', idle: 'Pictures you make can be bookmarked'},
    intake: 'Train your model',
}

export function chromeWords(theme: HouseTheme): WallChrome {
    return theme.chrome ?? DEFAULT_CHROME
}

/**
 * What a house does for a reader who is NOT signed in (plan `2026_09_17_ovid_one_wall.md`
 * § Decided 2-5). Absent — Toile, Narcis — the wall behaves for a visitor exactly as it does for a
 * member: the catalog pages in, a tap arms the column, and sign-in is asked for at Generate. Set,
 * the house is closed: the route feeds `Wall` a finite `AnonWallResponse` instead of a catalog page,
 * every cell of it is text-free by type (`AnonTile`), a tap opens the sign-in card, the ghost is the
 * door rather than the pencil, and the wall ends after a bounded coda.
 *
 * A third house gets all of that from these fields — there is no Ovid branch inside `Wall.tsx`. The
 * search field is the wall's own for a visitor too: the house's reader answers `?q=` with veiled
 * matches, and the field, the sign-in return and the arrival of `?basis=` are the wall's. A veiled
 * cell's picture is the `veil` path the wire carries; an open cell's goes through `tileImageUrl`.
 */
export type VisitorPolicy = {
    /** The accessible name a text-free cell carries — the cell's ONLY words, since no pack title,
     *  chip or slug ever reaches an `AnonTile`. */
    anonLabel: {open: string; veiled: string}
    /** The hover/focus door each kind of text-free cell carries — pointer hover and keyboard focus
     *  only, never on a phone (`Tile.module.scss`'s `(hover: hover)` guard). Both are presentation
     *  inside the cell's single button, never a second control. */
    anonDoor: {open: HouseDoor; veiled: HouseDoor}
    /** How many full rows of coda the wall ends on, at any breakpoint (`justify.boundedRows`). */
    codaRows: number
    /** Where the sign-in round-trip returns to, for a tap on the cell named by `tileId` — or on the
     *  ghost's door, which names none; `render` when the card was raised by the Render verb. The house
     *  owns the token's shape; `Wall` only carries it. */
    returnTo(tileId: number | null, render?: boolean): string
}

/**
 * Props `Column.tsx`/`Sheet.tsx` hand to `HouseTheme.identityPanel` (plan
 * `2026_09_17_ovid_wall_verbs.md` § Decided 5/6, lifted per `2026_09_18_ovid_wall_polish.md` § P4,
 * moved into the bar's own slot by plan `2026_09_18_armed_frame_budget.md` § D6) — the choose-state
 * slot, rendered only when a house provides one. A render prop rather than a component import:
 * `@core/wall/ui` has no dependency on `@product/*`, so the house's own identity UI (Ovid's
 * `MuseStrip`) is handed in fully formed, the same way `header`/`signIn` reach `Wall.tsx` today.
 *
 * One call site now (§ D6: "there is one placement: the bar's slot") — `Column`/`Sheet` call
 * `theme.identityPanel` exactly once per render and place its RETURN VALUE into whichever bar layout
 * `identityOpen`/`identityArmable` currently calls for (the collapsed chip's own slot, or the bar's
 * full-width choosing slot); the panel itself still decides which of its two shapes to draw, from
 * `open` and `identity` alone, same as before the two-call split it replaces.
 */
export type IdentityPanelProps = {
    /** The current identity, from `/mine` (or the loader's SSR seed) — never more than one poll
     *  tick stale. `Identity`'s own `'muse'` variant documents `slug: null` as the choose state
     *  (`contract.ts`); this is the one field `Column`/`Sheet` themselves read, via
     *  `chains.identityArmable`, to decide whether the verb may fire. */
    identity: Identity
    /** The panel's own write (`POST /mine/muse` at Ovid) lands here so the column/sheet re-renders
     *  in the same frame the fetch resolves — the same idiom `useMine.ts`'s `mergeTake`/
     *  `mergePinned` use for a write the caller just made, applied to the one field a chosen
     *  identity touches. */
    onIdentityChange: (identity: Identity) => void
    /** Whether the reader has explicitly asked to see the choosing strip — a tap on the bar's own
     *  chip, or a `/continue` refusal (409 `identity_required`) relayed by `Column`/`Sheet` from
     *  `Wall.tsx`'s own refusal counter. Owned by `Column`/`Sheet` now (plan P4 "lift the open state"
     *  — was a counter (`openSignal`) the panel translated into its own local `expanded` boolean;
     *  the translation moved up, so the panel reads a plain boolean and a house with a chosen
     *  identity but nothing to choose (museless is always shown regardless of this flag) still
     *  decides its OWN "show the strip at all" rule from `identity` + this flag, never from this
     *  flag alone. */
    open: boolean
    /** The panel's own request to expand (the chip was tapped) or collapse (a choice landed) —
     *  `Column`/`Sheet` hold the boolean this flows into. */
    onOpenChange: (open: boolean) => void
    /** The frame's preview while the strip is open (§ D6): the panel calls this with the highlighted
     *  muse's hero URL (hover/keyboard focus, else the chosen one) so the armed frame shows HER
     *  instead of the look on screen, and with `null` on close or unmount so the frame returns to its
     *  own picture. `Column`/`Sheet` hold the URL in state; core learns nothing about muses from this
     *  — it is an "identity preview", a string or nothing. */
    onPreview: (url: string | null) => void
    /** Opens the wall's intake overlay (`Wall`'s `renderIdentityIntake`); absent at a house with no intake. A panel
     *  with a door to making an identity of the reader's own (Ovid's + frame) calls it. */
    onOpenIntake?: () => void
    /** The panel asks for the bar's verb while it holds the bar for a question of its own (Ovid's "Let her go?"):
     *  `Column`/`Sheet` take the verb out of the bar until it lets go. */
    onHoldBar?: (held: boolean) => void
}

/**
 * What a picture allowance makes of the bar, all inside the bar's own row (it never adds height under it): `note`, two
 * short mono lines in the spacer between the identity chip and the pencil, `lit` in the accent; `verb`, when the
 * allowance stands in the verb's place (the pencil leaves with it): a label, disabled, or a document form posting
 * `fields` to `action`.
 */
export type BarAllowance = {
    note: readonly [ReactNode, ReactNode]
    lit?: boolean
    verb?: {
        label: ReactNode
        disabled?: boolean
        post?: {action: string; fields: Readonly<Record<string, string>>; onSubmit?: () => void}
    }
}

/** What the house's `allowanceBar` is told besides the state. */
export type BarAllowanceContext = {
    /** Back from the plan's checkout, until the next press. */
    welcome: boolean
    /** The wall's own address that restores the armed chain, for a checkout to return to. */
    returnTo: string
}

/** The address parameter a return from a checkout leaves, and the values that make it a welcome. */
export type AllowanceReturn = {param: string; values: readonly string[]; alsoStrip: readonly string[]}

/** The Share door's words (plan `2026_10_25_wall_share.md`). `text` carries `{url}` where the take's
 *  link goes (`share.ts`'s `SHARE_URL_MARK`); `null` = the system sheet carries the file alone. */
export type ShareWords = {
    door: string
    native: string
    download: string
    copyImage: string
    copyLink: string
    title: string
    text: string | null
}

/** A house's Share door: `link` = the house passes a take on by a page of its own (`/take/<uuid>`),
 *  `fileBase` = the saved file's name stem. */
export type HouseShare = {link: boolean; fileBase: string; words: ShareWords}

export type HouseTheme = {
    id: HouseId
    /** The house's facets (`products/<house>/wall/facets.ts`): the search panel's groups and the short labels of the
     *  facet tokens in the field. */
    facets: HouseFacets
    targetRowHeight: number
    gap: number
    /** The armed column's own bar width (plan `2026_09_18_armed_frame_budget.md` § D5) — the frame,
     *  the bar, the ratio row and the consent card all draw at this width, a house's own rather than
     *  the single `620` the column used to hard-code five times over. Portrait houses (Ovid) take a
     *  narrower bar than the wall's own picture aspect asks for (principle 2: "the column is one
     *  fused unit… portrait houses take a narrower bar"). `Column.tsx` sets it as the `--wall-bar-w`
     *  custom property on `.stage`; `frame.ts`'s `columnFrameBox` takes it as an argument. */
    armed: {barWidth: number}
    /** Whether the bar carries the ratio chip at all — Toile only (BRIEF § Ratio; askable at every
     *  basis since the operator decision of 2026-08-31, not the ghost path alone). */
    hasRatioPicker: boolean
    ratioKeys: readonly RatioKey[]
    defaultRatio: RatioKey
    /** Whether the bar hides its field behind a pencil door (plan `2026_09_18_ovid_wall_polish.md`
     *  § P3): unset at Toile, the field always on; `true` (Ovid, Narcis) reads identity slot ·
     *  pencil · verb at rest. Structural — the house uses the door at all. */
    pencil?: boolean
    /** Narrows when the door may be tapped (Narcis: ready only — operator 2026-09-22, "queuing and
     *  training buttons are enough"). Hides only the button; the chip-vs-field branch stays on
     *  `pencil`, so the identity chip keeps the field's place. Unset reads as always-on. */
    pencilWhen?: (identity: Identity | undefined) => boolean
    /** Wording — "add a direction…", "blank canvas", the verb — varies by house identity model.
     *  `generate`/`again` are the verb button's two words (`chains.verbLabel` picks between them);
     *  never read directly by the column/sheet other than through that pick. */
    verbs: {
        generate: string
        again: string
        /** The bar's words while a `{kind: 'model'}` identity is missing or training (`verbWord`
         *  below); unset at a house with no model to train. */
        model?: {missing: string; /** `missing`, once the visitor asked for a pack's nine. */ missingPack?: string; training: string; queued: string}
        /** The field's placeholder (2026-09-01 fix round, C2) — two strings, picked by
         *  `chains.fieldPlaceholder` off the step on screen: `onPicture` once a basis tile or a
         *  take is there to be changed, `onBlank` on a from-scratch chain with nothing rendered
         *  yet (the ghost's own armed state). */
        placeholder: {onPicture: string; onBlank: string}
        /** The ghost's words at rest, with nothing typed — a pair since board `FirstGlance`
         *  (validated 2026-09-18): the wall's one rule, then this cell's own act ("Start from
         *  nothing" at Toile, kept from `reports/2026_09_04_wall_first_words.md`). The first tile
         *  names the door to making from nothing, where the header's `onBlank` question asks what to
         *  see. Under a query the ghost shows the typed words instead; `Ghost.tsx`. Absent —
         *  `DEFAULT_GHOST_AT_REST`. */
        ghostAtRest?: GhostAtRest
        /** The armed ghost's frame before anything is typed. Absent — `DEFAULT_GHOST_ARMED`. */
        ghostArmed?: GhostArmed
        blankBasisLabel: string
        /** The pill's line for a cell whose `fail_reason` is `'refused'` (`contract.ts`) — the
         *  direction rewriter declined. A fixed, short, lowercase string per house, same
         *  vocabulary as every other pill line (`chains.ts`'s `errorLineFor`); the model's own
         *  refusal code never reaches the wire, let alone the words the user reads. */
        refused: string
    }
    /** Absent — `DEFAULT_CHROME`. */
    chrome?: WallChrome
    /** `TileSummary.image_key` is a full key under the wall_tiles ingest's own root
     *  (`pack-samples/toile/<slug>/<file>`, `ingest_toile.ts`'s own `imageKey` rule) — a DIFFERENT
     *  base than `TOILE_PACK_SAMPLES_BASE_URL` (`config.ts`), whose existing callers already bake
     *  that same prefix in and never prefix `image_key` again on top of it. Joining the two here
     *  would double the `pack-samples/toile/` prefix and 404 — verified against real tiles.
     *  Root-relative resolves it without importing `TOILE_PACK_SAMPLES_BASE_URL` at all. */
    tileImageUrl(imageKey: string): string
    /** `Take.image_key` is `<uuid>.webp` under the generations prefix — same-origin at Toile
     *  (`cloudfront-toile.tf` routes `/generations/*` to the shared content bucket by OAC; plan d
     *  amendment, board→back-end review item 9). */
    takeImageUrl(imageKey: string): string
    /** A rendering row's `n`th preview frame (`thinking/plans/2026_09_12_wall_preview_frames.md`,
     *  § Carrier "The contract") — root-relative, same door as `takeImageUrl`
     *  (`/previews/*` routed to the app bucket by the same OAC + CloudFront distribution, carrier
     *  plan § Files). Frames are named by ordinal, never by step — the browser never learns the
     *  step schedule. */
    previewImageUrl(rowId: string, n: number): string
    /** The choose-state slot — called once, in the bar's own slot (plan
     *  `2026_09_18_armed_frame_budget.md` § D6), and left to decide its own shape (the closed chip
     *  or the open strip) from `IdentityPanelProps.open`/`identity`; `undefined` for a house with no
     *  identity to choose (Toile: unchanged, no call made, byte-identical output). Ovid's is
     *  `MuseStrip` (`products/ovid/wall/MuseStrip.tsx`). */
    identityPanel?: (props: IdentityPanelProps) => ReactNode
    /** What the reader's picture allowance makes of the bar (`BarAllowance`); null leaves the bar as it is. Absent at
     *  a house whose writes have no allowance (Toile, Narcis): their 402 stays the pill's one word. */
    allowanceBar?: (state: AllowanceState, context: BarAllowanceContext) => BarAllowance | null
    /** The address a return from the allowance's checkout carries; the wall reads it once, strips it, and lets
     *  `allowanceBar` say `welcome` until the next press. */
    allowanceReturn?: AllowanceReturn
    /** Whether a wall_tiles cell's hover label links to the pack page it names (`Wall.tsx`'s
     *  `labelHref`, `Tile.tsx`'s own `label`/`labelHref` split) — a house decision, not a hard-coded
     *  `/${packSlug}` at every call site: `/<packSlug>` 404s at Ovid, whose surface cut serves only
     *  the wall itself. `null` renders the label
     *  as plain text (`Tile.tsx` already supports `labelHref: null` for its from-scratch cells, which
     *  have no page either). A function of the slug, not a boolean, so a house could answer
     *  differently per slug later without a second `HouseTheme` field — every house today answers
     *  the same way for every slug. */
    tileLabelHref(packSlug: string): string | null
    /** The Share door on the reader's own done take. Absent = no door. */
    share?: HouseShare
    /** This house's policy for a reader who is not signed in — see `VisitorPolicy`. Omitted by
     *  every house whose wall is open to a visitor (Toile, Narcis), which is what keeps their
     *  behaviour and their markup exactly as they are. */
    visitor?: VisitorPolicy
    /** The first Generate of a signed-out reader writes; the server holds the allowance
     *  (`HouseAdapter.freePick`). Absent = sign-in gates every Generate. */
    freePick?: true
}

/** `theme.pencil` says the house uses the door; `pencilWhen` narrows it by the current identity. */
export function pencilOn(theme: HouseTheme, identity: Identity | undefined): boolean {
    if (!theme.pencil) return false
    return theme.pencilWhen ? theme.pencilWhen(identity) : true
}

/** The verb button's word. `verbs.model` (a house with a trainable model) wins while the model is
 *  missing or training — `queued` only while this chain's own row is in flight, the same
 *  optimistic window `holdVerb` keeps "Again" in. Otherwise `verbs[verbState]`. */
export function verbWord(
    theme: HouseTheme, verbState: VerbState, identity: Identity | undefined, working: boolean,
): string {
    const model = theme.verbs.model
    if (model && identity?.kind === 'model') {
        if (identity.state === 'missing') return model.missing
        if (identity.state === 'training') return working ? model.queued : model.training
    }
    return theme.verbs[verbState]
}
